/* eslint-disable max-lines */
import PropTypes from 'prop-types';
import { createRef, PureComponent } from 'react';
import { connect } from 'react-redux';

import { STORE_SWITCHER_HEADER_ID } from 'Component/StoreSwitcher/StoreSwitcher.config';
import { showNotification } from 'Store/Notification/Notification.action';
import { closePopup, showPopup } from 'Store/Popup/Popup.action';
import StoresDispatcher from 'Store/Stores/Stores.dispatcher';
import history from 'Util/History';

import StoreSelector from './StoreSelector.component';
import { HEADER_HEIGHT, TOUCH_TIMEOUT } from './StoreSelector.config';

import './StoreSelector.style';

/** @namespace Bodypwa/Component/StoreSelector/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    device: state.ConfigReducer.device,
    currentStoreCode: state.ConfigReducer.default_country,
    hreflangs: state.MetaReducer.hreflangs,
    countries: state.StoresReducer.countries,
    statusCode: state.MetaReducer.status_code,
    isNoHreflangs: !!state.MetaReducer.isNoHreflangs
});

/** @namespace Bodypwa/Component/StoreSelector/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    showErrorNotification: (message) => dispatch(showNotification('error', message)),
    showPopup: (id, payload) => dispatch(showPopup(id, payload)),
    closePopup: (id, payload) => dispatch(closePopup(id, payload)),
    getCountries: () => StoresDispatcher.handleData(dispatch)
});
/** @namespace Bodypwa/Component/StoreSelector/Container */
export class StoreSelectorContainer extends PureComponent {
    static propTypes = {
        showPopup: PropTypes.func.isRequired,
        closePopup: PropTypes.func.isRequired,
        toggleStoreSelector: PropTypes.func.isRequired,
        isOpened: PropTypes.bool.isRequired,
        // eslint-disable-next-line react/forbid-prop-types
        device: PropTypes.object.isRequired,
        searchValue: PropTypes.string.isRequired,
        selectedCountry: PropTypes.shape({
            label: PropTypes.string,
            names: PropTypes.arrayOf(PropTypes.string),
            code: PropTypes.string,
            url: PropTypes.string
        }).isRequired,
        hreflangs: PropTypes.arrayOf(PropTypes.shape({
            code: PropTypes.string,
            url: PropTypes.string
        })).isRequired,
        isSimple: PropTypes.bool.isRequired,
        currentStoreCode: PropTypes.string,
        storeSelectorId: PropTypes.string.isRequired,
        getCountries: PropTypes.func.isRequired,
        countries: PropTypes.arrayOf(PropTypes.shape({
            label: PropTypes.string,
            names: PropTypes.arrayOf(PropTypes.string),
            locale: PropTypes.string,
            code: PropTypes.string,
            url: PropTypes.string,
            currency: PropTypes.string,
            hreflang: PropTypes.string
        })).isRequired,
        statusCode: PropTypes.string.isRequired,
        isNoHreflangs: PropTypes.bool.isRequired
    };

    headerRef = createRef();

    static defaultProps = {
        currentStoreCode: 'default'
    };

    state = {
        searchValue: '',
        selectedCountry: {
            label: '',
            names: [],
            code: '',
            url: ''
        },
        isFitScreen: false,
        isActive: false,
        directRedirect: false,
        popUpHeight: window.innerHeight
    };

    searchRef = createRef();

    containerFunctions = {
        toggleStoreSelectPopup: this.toggleStoreSelectPopup.bind(this),
        onStoreSwitcherClick: this.onStoreSwitcherClick.bind(this),
        onSearchChange: this.onSearchChange.bind(this),
        selectCountry: this.selectCountry.bind(this),
        goToShop: this.goToShop.bind(this),
        fitScreen: this.fitScreen.bind(this),
        resetSearch: this.resetSearch.bind(this),
        setActive: this.setActive.bind(this),
        enableScrolling: this.enableScrolling.bind(this),
        disableScrolling: this.disableScrolling.bind(this),
        stopScroll: this.stopScroll.bind(this)
    };

    componentDidMount() {
        const { getCountries } = this.props;
        getCountries();
    }

    componentDidUpdate() {
        const {
            isActive, searchValue, selectedCountry, directRedirect
        } = this.state;
        const { currentStoreCode, isSimple } = this.props;
        if (!isActive && !searchValue) {
            // This is required since the behavior trigers the click on the background and closes the modal
            setTimeout(() => {
                this.fitScreen(false);
            }, TOUCH_TIMEOUT);
        }

        if (isSimple && !selectedCountry.label && currentStoreCode) {
            this.getCurrentLabel(currentStoreCode);
        }
        if (directRedirect) {
            this.goToShop();
        }

        setTimeout(() => {
            this.setPopUpHeight();
            // eslint-disable-next-line no-magic-numbers
        }, 15);
    }

    setPopUpHeight() {
        const { isFitScreen } = this.state;
        const height = window.visualViewport?.height || window.innerHeight;
        const popUpHeight = isFitScreen ? height + HEADER_HEIGHT : height - HEADER_HEIGHT;
        const { popUpHeight: prevPopUpHeight } = this.state;
        if (popUpHeight !== prevPopUpHeight) {
            this.setState({ popUpHeight });
        }
    }

    containerProps() {
        const {
            device,
            isSimple = false,
            storeSelectorId,
            countries,
            hreflangs,
            statusCode,
            isNoHreflangs
        } = this.props;

        const {
            searchValue, selectedCountry, isFitScreen, isActive, popUpHeight
        } = this.state;

        return {
            device,
            searchValue,
            selectedCountry,
            isFitScreen,
            isActive,
            countries,
            storeSelectorId,
            searchRef: this.searchRef,
            headerRef: this.headerRef,
            isSimple,
            popUpHeight,
            hreflangs,
            statusCode,
            isNoHreflangs
        };
    }

    getCurrentLabel(storeCode) {
        const { countries } = this.props;
        const currentCountry = countries.filter(
            ({ code }) => code === storeCode
        );

        if (!currentCountry.length) {
            return;
        }

        this.selectCountry(currentCountry[0], false);
    }

    fitScreen(toggle = true) {
        const { device } = this.props;
        this.setState({ isFitScreen: toggle && device.isMobile });
    }

    setActive(isActive = true) {
        this.setState({ isActive });
    }

    onStoreSwitcherClick() {
        const { toggleStoreSelector, isOpened } = this.props;
        const isActive = {};
        isActive[STORE_SWITCHER_HEADER_ID] = true;
        toggleStoreSelector({ ...isOpened, ...isActive });
    }

    onSearchChange(e) {
        const { value: searchValue } = e.target;
        const { headerRef } = this;
        if (headerRef.current) {
            headerRef.current.parentElement.scrollTop = 0;
        }
        this.setState({ searchValue });
    }

    toggleStoreSelectPopup(show = true) {
        const {
            showPopup, closePopup, storeSelectorId, hreflangs = [], countries = [], statusCode, isNoHreflangs
        } = this.props;

        if (hreflangs !== null
            && ((hreflangs.length === 0 && statusCode !== '404') || countries.length === 0) && !isNoHreflangs) {
            return;
        }

        if (show) {
            showPopup(storeSelectorId, {
                title: __('Shipping country')
            });
        } else {
            closePopup(storeSelectorId, {});
        }
    }

    selectCountry(selectedCountry, directRedirect = false) {
        this.setState({ searchValue: '' });
        this.setState({ selectedCountry, directRedirect });
        this.toggleStoreSelectPopup(false);
    }

    goToShop() {
        const {
            selectedCountry: {
                url, locale, hreflang: currentHrefLang
            }
        } = this.state;
        const { hreflangs = [] } = this.props;
        const { location: { search, pathname = '' } = {} } = history || {};

        const hrefLangPath = hreflangs?.filter((hreflang) => hreflang.code === currentHrefLang) || url;

        const redirectUrl = typeof hrefLangPath !== 'string' && hrefLangPath[0]
            ? `${hrefLangPath[0]?.url}${search}` : `${url}${pathname.substring(1)}/${search}`;

        if (locale) {
            window
                .location
                .assign(redirectUrl);
        } else {
            this.toggleStoreSelectPopup(true);
        }
    }

    resetSearch() {
        this.setState({ searchValue: '' });
    }

    stopScroll() {
        const input = this.searchRef.current;
        if (input) {
            const top = input.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    }

    disableScrolling() {
        const { stopScroll } = this;
        document.addEventListener('touchend', stopScroll.bind(this));
        setTimeout(stopScroll.bind(this), TOUCH_TIMEOUT);
    }

    enableScrolling() {
        const { stopScroll } = this;
        document.removeEventListener('touchend', stopScroll.bind(this));
    }

    render() {
        return (
            <StoreSelector
              { ...this.containerFunctions }
              { ...this.containerProps() }
            />
        );
    }
}
export default connect(mapStateToProps, mapDispatchToProps)(StoreSelectorContainer);
